import { computed, nextTick, onScopeDispose, onMounted, ref, toValue, unref, watch } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { isBoolean, isNil, isObject, keys, pick } from 'lodash';

import { transformTaskApiResponseToViewData } from '@satellite/helpers/yardTaskUtils';
import { matchSocketEventToEntity, SocketEventMatch } from '@satellite/helpers/socketEventMatch';
import { useEventHub } from '@satellite/composables/useEventHub';
import yardTasksService from '@satellite/services/yard-tasks-service';

import { DEFAULT_INVALIDATE_OPTIONS, queryKeys } from './queryKeys';
import {
  EntityPresenterV2Mode,
  isEntityPresenterV2QueryEnabled,
  isLazyLiveMode,
  isStaticMode,
  subscribesToEventHub
} from './presenterV2EntityMode';

import type { ComputedRef, Ref } from 'vue';
import type { Maybe, TaskApiResponseData } from '@nova/core';
import type { PresenterPlaceholderData } from './presenterV2Placeholder';
import type { YardTaskDetails, YardTaskEventPayload } from './yardTaskPresenterV2Shared';

export interface UseYardTaskFetcherV2Options {
  /**
   * Task ID to fetch. Can be a ref for reactivity.
   * Optional if initialTaskData (with id) is provided.
   */
  taskId?: Ref<Maybe<string>> | ComputedRef<Maybe<string>>;

  /**
   * Optional initial task data used as initialData for the query.
   * Effective id is derived from taskId or initialTaskData.id (explicit taskId wins; mismatch triggers a warning).
   *
   * Note: TanStack Query will add this value to the query cache as `initialData`.
   */
  initialTaskData?:
    | Ref<Maybe<YardTaskDetails>>
    | ComputedRef<Maybe<YardTaskDetails>>
    | YardTaskDetails;

  /**
   * Optional incomplete task data shown as placeholderData while the query fetches (replaced when fetch completes).
   */
  placeholderTaskData?:
    | Ref<Maybe<PresenterPlaceholderData<YardTaskDetails>>>
    | ComputedRef<Maybe<PresenterPlaceholderData<YardTaskDetails>>>
    | PresenterPlaceholderData<YardTaskDetails>;

  /**
   * When true, the query runs without viewport gating (isInViewport starts true, onIntersect is no-op).
   * Use when the composable is used as a data source without a mounted element (e.g. no v-intersect).
   * Default: false
   */
  headless?: boolean;

  /**
   * Presenter mode.
   *
   * - `live`: fetches when enabled and subscribes to EventHub updates (default)
   * - `lazy-live`: like `live`, but no fetch until first matching EventHub event, `refresh()`, or `invalidate()`
   * - `static`: disables fetching/subscriptions entirely and returns `staticTaskData` only
   */
  mode?: EntityPresenterV2Mode;

  /**
   * Data source for `mode: 'static'`.
   *
   * Use for historical data that should be shown "as is" (no fetch, no subscriptions, no invalidations).
   * Must be complete because it fully replaces the query result.
   */
  staticTaskData?:
    | Ref<Maybe<YardTaskDetails>>
    | ComputedRef<Maybe<YardTaskDetails>>
    | YardTaskDetails;

  /**
   * Stale time in milliseconds.
   * Default: Infinity (never stale, until a Subspace event invalidates the query; manual refresh is always available)
   */
  staleTime?: number;

  /**
   * Callback for when task is updated.
   */
  onUpdateReceived?: (task: YardTaskDetails) => void;
}

/**
 * TODO: (welington) consolidate/deduplicate the useYardTaskFetcher, useYardTaskPresenter, useYardTask (will be tackled on OD-3238)
 */

export function useYardTaskFetcherV2(options: UseYardTaskFetcherV2Options) {
  const {
    taskId,
    initialTaskData,
    placeholderTaskData,
    headless = false,
    mode = EntityPresenterV2Mode.Live,
    staticTaskData,
    staleTime = Infinity,
    onUpdateReceived
  } = options;

  const eventHub = useEventHub();
  const queryClient = useQueryClient();

  const effectiveTaskId = computed((): string | null => {
    const explicitId = unref(taskId);
    const fromObject = toValue(initialTaskData)?.id ?? null;

    if (explicitId) {
      if (fromObject && fromObject !== explicitId) {
        console.warn(
          '[useYardTaskFetcherV2] taskId mismatch:',
          `explicit taskId (${explicitId}) does not match task.id (${fromObject}). Using explicit taskId.`
        );
      }
      return explicitId;
    }
    return fromObject;
  });

  const initialTask = computed(() => toValue(initialTaskData) ?? undefined);
  const placeholderTask = computed(() => toValue(placeholderTaskData) ?? undefined);
  const staticTask = computed(() => toValue(staticTaskData) ?? undefined);

  if (
    isStaticMode(mode) &&
    (!isNil(toValue(initialTaskData)) || !isNil(toValue(placeholderTaskData)))
  ) {
    console.warn(
      '[useYardTaskFetcherV2] mode=static ignores initialTaskData/placeholderTaskData. Use staticTaskData.'
    );
  }

  if (isLazyLiveMode(mode) && !isNil(toValue(initialTaskData))) {
    console.warn(
      '[useYardTaskFetcherV2] mode=lazy-live does not use initialTaskData for the query cache; use placeholderTaskData for partial UI.'
    );
  }

  const lazyLiveUnlocked = ref(!isLazyLiveMode(mode));

  const wakeLazyLive = () => {
    if (isLazyLiveMode(mode) && !lazyLiveUnlocked.value) {
      lazyLiveUnlocked.value = true;
    }
  };

  const isInViewport = ref(headless);
  const pendingInvalidation = ref(false);

  const invalidateRelatedMoveKeys = (event: YardTaskEventPayload) => {
    if (event?.child?.assetContainerId) {
      queryClient.invalidateQueries(
        { queryKey: queryKeys.assetContainer(event.child.assetContainerId) },
        DEFAULT_INVALIDATE_OPTIONS
      );
    }

    if (event?.child?.originSpotId) {
      queryClient.invalidateQueries(
        { queryKey: queryKeys.spotDetail(event.child.originSpotId) },
        DEFAULT_INVALIDATE_OPTIONS
      );
    }

    if (event?.child?.destinationSpotId) {
      queryClient.invalidateQueries(
        { queryKey: queryKeys.spotDetail(event.child.destinationSpotId) },
        DEFAULT_INVALIDATE_OPTIONS
      );
    }
  };

  const handleIntersect = (entry?: { isIntersecting?: boolean } | boolean) => {
    if (headless) {
      return;
    }

    const isIntersecting = isBoolean(entry)
      ? entry
      : isObject(entry)
        ? (entry.isIntersecting ?? true)
        : true;
    isInViewport.value = isIntersecting;

    if (isIntersecting && pendingInvalidation.value) {
      pendingInvalidation.value = false;
      invalidate();
    }
  };

  const taskQuery = useQuery<YardTaskDetails | null, Error, YardTaskDetails | null>({
    queryKey: computed(() => (effectiveTaskId.value ? queryKeys.task(effectiveTaskId.value) : [])),
    queryFn: async ({ signal }) => {
      if (!effectiveTaskId.value) {
        return null;
      }

      const task = (await yardTasksService.getYardTask(effectiveTaskId.value, {
        requestConfig: { signal },
        enableCache: true
      })) as TaskApiResponseData;

      return transformTaskApiResponseToViewData(task);
    },
    enabled: computed(() =>
      isEntityPresenterV2QueryEnabled({
        mode,
        hasEffectiveId: Boolean(effectiveTaskId.value),
        isInViewport: isInViewport.value,
        lazyLiveUnlocked: lazyLiveUnlocked.value
      })
    ),
    initialData: computed(() =>
      isStaticMode(mode) || isLazyLiveMode(mode) ? undefined : initialTask.value
    ),
    placeholderData: computed(() => {
      if (isStaticMode(mode)) {
        return undefined;
      }
      return placeholderTask.value as YardTaskDetails | undefined;
    }),
    staleTime,
    refetchOnWindowFocus: false
  });

  const taskState = ref<YardTaskDetails | null>(null);

  if (!isStaticMode(mode)) {
    watch(
      () => taskQuery.data.value,
      newTask => {
        taskState.value = newTask ?? null;
      },
      { immediate: true }
    );
  }

  const task = computed((): YardTaskDetails | null => {
    if (isStaticMode(mode)) {
      if (!staticTask.value) {
        console.warn('[useYardTaskFetcherV2] mode=static requires staticTaskData.');
        return null;
      }

      return staticTask.value;
    }

    return taskState.value;
  });

  const isLoading = computed(() => (isStaticMode(mode) ? false : taskQuery.isLoading.value));
  const isFetching = computed(() => (isStaticMode(mode) ? false : taskQuery.isFetching.value));
  const error = computed(() => (isStaticMode(mode) ? null : taskQuery.error.value));

  const invalidate = () => {
    if (isStaticMode(mode)) {
      return;
    }

    wakeLazyLive();

    if (effectiveTaskId.value) {
      queryClient.invalidateQueries(
        { queryKey: queryKeys.task(effectiveTaskId.value) },
        DEFAULT_INVALIDATE_OPTIONS
      );
    }
  };

  const refresh = async () => {
    if (isStaticMode(mode)) {
      return;
    }

    wakeLazyLive();
    await nextTick();
    await taskQuery.refetch();
  };

  const handleYardTaskEvent = (event: YardTaskEventPayload) => {
    const match = matchSocketEventToEntity(event, effectiveTaskId.value);

    if (match === SocketEventMatch.None) {
      return;
    }

    wakeLazyLive();

    // A collapsed event's payload is another task's, so it can only be refetched from, never read.
    if (match === SocketEventMatch.Collapsed) {
      if (isInViewport.value) {
        invalidate();
      } else {
        pendingInvalidation.value = true;
      }

      return;
    }

    if (event.type === 'move') {
      invalidateRelatedMoveKeys(event);
    }

    if (!isInViewport.value) {
      pendingInvalidation.value = true;
      return;
    }

    const previousTask = taskState.value;
    if (!previousTask) {
      invalidate();
      return;
    }

    const patchedFields = pick(
      transformTaskApiResponseToViewData(event as Maybe<TaskApiResponseData>),
      keys(previousTask)
    ) as PresenterPlaceholderData<YardTaskDetails>;
    taskState.value = {
      ...previousTask,
      ...patchedFields
    } as YardTaskDetails;

    // TODO: (welington) should we call invalidate here too?
  };

  watch(task, newTask => {
    if (onUpdateReceived && newTask) {
      onUpdateReceived(newTask);
    }
  });

  watch(effectiveTaskId, (newId, oldId) => {
    if (newId !== oldId) {
      pendingInvalidation.value = false;
      lazyLiveUnlocked.value = !isLazyLiveMode(mode);
    }
  });

  onMounted(() => {
    if (subscribesToEventHub(mode)) {
      eventHub.on('update-YardTask', handleYardTaskEvent);
      eventHub.on('delete-YardTask', handleYardTaskEvent);

      // TODO: (welington) we also need to watch for related asset and spot events, depending on the task type
    }
  });

  onScopeDispose(() => {
    if (subscribesToEventHub(mode)) {
      eventHub.off('update-YardTask', handleYardTaskEvent);
      eventHub.off('delete-YardTask', handleYardTaskEvent);
    }
  });

  return {
    data: task,

    isLoading,
    isFetching,
    error,

    refresh,
    invalidate,

    onIntersect: handleIntersect,

    query: taskQuery
  };
}
